import React from 'react';
import { Bot, Folder, PanelLeft, PlugZap, Radio, Search, SquarePen, Users } from 'lucide-react';

// The dashboard-icons CDN is missing marks for several of these (HubSpot,
// Snowflake, QuickBooks, Intercom), which rendered as broken images. Resolving
// through each brand's own domain is the pattern used elsewhere in the project
// and covers every tool we reference.
const TOOL_DOMAINS: Record<string, string> = {
  slack: 'slack.com',
  gmail: 'mail.google.com',
  notion: 'notion.so',
  salesforce: 'salesforce.com',
  linear: 'linear.app',
  hubspot: 'hubspot.com',
  snowflake: 'snowflake.com',
  stripe: 'stripe.com',
  quickbooks: 'quickbooks.intuit.com',
  figma: 'figma.com',
  intercom: 'intercom.com',
  ahrefs: 'ahrefs.com',
  github: 'github.com',
  sentry: 'sentry.io',
  mailchimp: 'mailchimp.com',
  granola: 'granola.ai',
  // AI surfaces the connections are exposed to.
  chatgpt: 'openai.com',
  claude: 'claude.ai',
  grok: 'x.ai'
};
export const toolLogo = (slug: string) => `https://www.google.com/s2/favicons?domain=${TOOL_DOMAINS[slug] ?? `${slug}.com`}&sz=64`;

/**
 * Shared chrome for every real-product screenshot on the page. One frame means
 * Connections, the platform primitives and the brain all read as the same
 * application rather than four unrelated illustrations.
 */
export function AppChrome({
  children,
  sidebar = true,
  active,
  className = ''
}: {
  children: React.ReactNode;
  sidebar?: boolean;
  active?: string;
  className?: string;
}) {
  // No browser bar: the fake URL and window chrome added a frame around the
  // frame without telling anyone anything they didn't already know.
  return <div className={`overflow-hidden rounded-[20px] border border-[#E7E0D4] bg-white shadow-[0_40px_100px_-40px_rgba(43,33,26,0.45)] ${className}`} data-id="element-189" data-mp="3ejmn3wlw2qu7">
      <div className="flex min-h-[420px]" data-id="element-190" data-mp="1cs9mhjqyf53y">
        {sidebar && <AppSidebar active={active} data-id="element-191" data-mp="1cpx4j6l74uqw" />}
        <div className="min-w-0 flex-1 bg-white" data-id="element-192" data-mp="17sdkfq1thnr1">{children}</div>
      </div>
    </div>;
}
const SIDEBAR_TRACKS = ['Onboarding revamp', 'Q3 pipeline', 'Brand refresh', 'Renewals'];
const SIDEBAR_CHATS = ['Inbox catch-up', 'Renewal risk', 'Board deck notes'];
function AppSidebar({
  active
}: {
  active?: string;
}) {
  return <aside className="hidden w-[196px] shrink-0 flex-col border-r border-[#EFEAE1] bg-[#FCFAF6] px-2.5 py-3 lg:flex" data-id="element-193" data-mp="1hy4ze5xsrofm">
      <div className="mb-3 flex items-center justify-between px-1.5" data-id="element-194" data-mp="1mw6igqv83nr3">
        <span className="flex items-center gap-2" data-id="element-195" data-mp="2amkcgmxdbwob">
          <span className="flex size-6 items-center justify-center rounded-lg bg-[#134E4A] text-[10px] font-bold text-white" data-id="element-196" data-mp="2ajogv58ztk31">
            BW
          </span>
          <span className="text-[12.5px] font-semibold text-[#2B211A]" data-id="element-197" data-mp="20mjqsuc114gy">
            Brightwave
          </span>
        </span>
        <PanelLeft size={13} className="text-neutral-300" data-id="element-198" data-mp="25oivc7ji67fh" />
      </div>
      <div className="mb-3 flex items-center gap-2 rounded-xl border border-[#E7E0D4] bg-white px-2.5 py-1.5" data-id="element-199" data-mp="p7emxn05szvr">
        <SquarePen size={12} className="text-neutral-400" data-id="element-200" data-mp="u8u619is0wbi" />
        <span className="text-[12px] font-semibold text-[#2B211A]" data-id="element-201" data-mp="mn8f348fcp9x">
          New chat
        </span>
      </div>
      <div className="mb-1 flex items-center gap-2 rounded-lg bg-white px-2 py-1.5 ring-1 ring-[#EFEAE1]" data-id="element-202" data-mp="hnlta6ej60pi">
        <Search size={11} className="text-neutral-300" data-id="element-203" data-mp="vn50mwzip80r" />
        <span className="text-[11.5px] text-neutral-400" data-id="element-204" data-mp="qpbsvzikg5yd">Search</span>
      </div>
      <SidebarGroup label="Manage" items={MANAGE_ITEMS} active={active} data-id="element-205" data-mp="qnimmlbv7nqw" />
      <p className="mt-3 px-2 pb-1 text-[9.5px] font-bold uppercase tracking-[0.09em] text-neutral-400" data-id="element-206" data-mp="30afryysg6mr">
        Tracks
      </p>
      <ul data-id="element-207" data-mp="3v1qjmvz5pmos">
        {SIDEBAR_TRACKS.map(t => <li key={t} className="flex items-center gap-2 rounded-lg px-2 py-[5px] text-[12px] font-medium text-neutral-500" data-id="element-208" data-mp="cpu0la62aak9">
            <Folder size={11} strokeWidth={1.9} className="shrink-0 text-neutral-300" data-id="element-209" data-mp="7xapcjuujrim" />
            <span className="truncate" data-id="element-210" data-mp="3gdcjist4kcbx">{t}</span>
          </li>)}
      </ul>
      <p className="mt-3 px-2 pb-1 text-[9.5px] font-bold uppercase tracking-[0.09em] text-neutral-400" data-id="element-211" data-mp="3bg40yr42e6a5">
        Chats
      </p>
      <ul data-id="element-212" data-mp="3p3m5af4q7b1y">
        {SIDEBAR_CHATS.map(c => <li key={c} className="truncate rounded-lg px-2 py-[5px] text-[12px] font-medium text-neutral-500" data-id="element-213" data-mp="3p0ggl100eb6r">
            {c}
          </li>)}
      </ul>
    </aside>;
}
const MANAGE_ITEMS = [{
  label: 'Assistants',
  icon: Bot
}, {
  label: 'Pulses',
  icon: Radio
}, {
  label: 'Connections',
  icon: PlugZap
}, {
  label: 'Groups',
  icon: Users
}];
function SidebarGroup({
  label,
  items,
  active
}: {
  label: string;
  items: Array<{
    label: string;
    icon: React.ElementType;
  }>;
  active?: string;
}) {
  return <>
      <p className="mt-3 px-2 pb-1 text-[9.5px] font-bold uppercase tracking-[0.09em] text-neutral-400" data-id="element-214" data-mp="ll1cegebavtj">
        {label}
      </p>
      <ul data-id="element-215" data-mp="qjcn0tcflact">
        {items.map(item => {
        const isActive = active === item.label;
        return <li key={item.label} className={`flex items-center gap-2 rounded-lg px-2 py-[5px] text-[12px] ${isActive ? 'bg-[#F1ECE3] font-semibold text-[#2B211A]' : 'font-medium text-neutral-500'}`} data-id="element-216" data-mp="2ne8yq9scedox">
              <item.icon size={11} strokeWidth={1.9} className={`shrink-0 ${isActive ? 'text-[#2B211A]' : 'text-neutral-300'}`} data-id="element-217" data-mp="19hpieiokl5n2" />
              <span className="truncate" data-id="element-218" data-mp="14o34yoal0xq7">{item.label}</span>
            </li>;
      })}
      </ul>
    </>;
}